import { ReactNode } from "react";

import {
  type BulkAction,
  type BulkActionHandlersByType,
  type BulkActionsQuery,
  BulkActionsProvider as SharedBulkActionsProvider,
} from "@punto/frontend-shared/components/bulk-actions";

import { $api } from "@/lib/api";

import { autoDownloadFinishedReport } from "./auto-download-finished-report";

const REPORT_BULK_ACTIONS_PATH = "/v1/reports/bulk-actions";

/**
 * Only the ZIP download (P-2574) has anything to do when it settles: it starts
 * downloading by itself. A finished *request* batch has no ZIP to chase, so it
 * has no entry. Module scope, so the reference is stable across renders.
 */
const REPORT_DOWNLOAD_TYPE = "bulk_download_reports";

const HANDLERS_BY_TYPE: BulkActionHandlersByType = {
  [REPORT_DOWNLOAD_TYPE]: { onSettled: autoDownloadFinishedReport },
};

/**
 * The poll the shared provider drives, declared at module scope as a real
 * `use*` hook rather than built inside the component: the shared provider calls
 * it unconditionally on every render, so hook order stays stable, and the
 * reference is stable without a `useCallback` wrapper.
 */
const useReportBulkActionsQuery: BulkActionsQuery = ({
  enabled,
  refetchInterval,
}) =>
  $api.useQuery(
    "get",
    REPORT_BULK_ACTIONS_PATH,
    {},
    {
      enabled,
      refetchInterval: ({ state }) =>
        refetchInterval(state.data as BulkAction[] | undefined),
    },
  ) as { data: BulkAction[] | undefined };

/**
 * Portus's binding of the shared bulk-actions provider. Both of Portus's bulk
 * actions are report-related — the ZIP download (P-2574) and the bulk report
 * request (P-2572) — so one poll of `GET /v1/reports/bulk-actions` covers them.
 * No detail routes: neither has an entity page to link an incident to.
 */
export const BulkActionsProvider = ({ children }: { children: ReactNode }) => (
  <SharedBulkActionsProvider
    useActionsQuery={useReportBulkActionsQuery}
    actionsQueryPath={REPORT_BULK_ACTIONS_PATH}
    handlersByType={HANDLERS_BY_TYPE}
  >
    {children}
  </SharedBulkActionsProvider>
);
